import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * The slice of `ChatPageContext` a *page* is allowed to contribute. Route
 * derivation (`page` / `entityType` / `entityIds`) stays the baseline and is
 * never publishable — see `useAssistPageContext`.
 *
 * Everything here is an unverified display hint: the assistant treats it as
 * wording only and reads real data through its own credentialed tools. Publish
 * nothing sensitive — no emails, no tokens, no PII beyond what the page already
 * shows on screen.
 */
export type PublishedAssistContext = {
  /** Human-readable name of what the user is looking at, e.g. "Senior Accountant". */
  label?: string;
  /** Extra display-only hints, e.g. `{ status: "Draft", division: "Finance" }`. */
  attributes?: Record<string, string>;
};

const EMPTY: PublishedAssistContext = {};

type Entry = { key: symbol; value: PublishedAssistContext };

type AssistPageContextStore = {
  published: PublishedAssistContext;
  publish: (key: symbol, value: PublishedAssistContext) => void;
  clear: (key: symbol) => void;
};

const noop = () => undefined;

/*
  The default value makes the provider optional: a tree without it (unit tests,
  storybook, the public shell) simply sees nothing published rather than
  throwing. That is why this is a plain React context and not `createContext`
  from @compono/ui, which requires a provider.
*/
const AssistPageContextStoreContext = createContext<AssistPageContextStore>({
  published: EMPTY,
  publish: noop,
  clear: noop,
});

/** Drops blank values so a page can pass partially-loaded data unconditionally. */
const normalise = (context: PublishedAssistContext) => {
  const trimmedLabel =
    typeof context.label === "string" ? context.label.trim() : "";

  const names = Object.keys(context.attributes || {}).sort();
  const attributes: Record<string, string> = {};
  let attributeCount = 0;
  for (const name of names) {
    const raw = (context.attributes || {})[name];
    const value = typeof raw === "string" ? raw.trim() : "";
    if (!value) continue;
    attributes[name] = value;
    attributeCount += 1;
  }

  return {
    ...(trimmedLabel ? { label: trimmedLabel } : {}),
    ...(attributeCount > 0 ? { attributes } : {}),
  };
};

/** Stable identity for a normalised value, so inline object literals don't loop. */
const signatureOf = (context: PublishedAssistContext) =>
  JSON.stringify([context.label ?? null, context.attributes ?? null]);

type ProviderProps = { children: React.ReactNode };

/**
 * Holds whatever the current page has published for the AI assistant.
 *
 * Must wrap BOTH the routed page tree (the publishers) and `ChatAiMount` (the
 * consumer, via `useAssistPageContext`) — see `Private` in `App.tsx`.
 */
export const AssistPageContextProvider = ({ children }: ProviderProps) => {
  /*
    An ordered registry rather than a single value. Each caller owns a private
    symbol, so an unmount can only ever remove *its own* entry: if React commits
    the new page's publish before the old page's cleanup (sibling swaps, or any
    future concurrent ordering), the stale cleanup cannot wipe the fresh value.
    The most recently published entry wins.
  */
  const [entries, setEntries] = useState<Entry[]>([]);

  const publish = useCallback(
    (key: symbol, value: PublishedAssistContext) => {
      setEntries((current) => {
        const existing = current.find((entry) => entry.key === key);
        if (existing && existing.value === value) {
          // Already the winner with this exact value — no state change, so no
          // render loop when several publishers re-run their effects.
          if (current[current.length - 1] === existing) return current;
        }
        return [
          ...current.filter((entry) => entry.key !== key),
          { key, value },
        ];
      });
    },
    [setEntries],
  );

  const clear = useCallback(
    (key: symbol) => {
      setEntries((current) =>
        current.some((entry) => entry.key === key)
          ? current.filter((entry) => entry.key !== key)
          : current,
      );
    },
    [setEntries],
  );

  const published =
    entries.length > 0 ? entries[entries.length - 1].value : EMPTY;

  const value = useMemo<AssistPageContextStore>(
    () => ({ published, publish, clear }),
    [published, publish, clear],
  );

  return (
    <AssistPageContextStoreContext.Provider value={value}>
      {children}
    </AssistPageContextStoreContext.Provider>
  );
};

/** What the current page has published. `{}` when nothing has, or with no provider. */
export const usePublishedAssistContext = (): PublishedAssistContext =>
  useContext(AssistPageContextStoreContext).published;

/**
 * Publishes human-readable context for the page the user is on. Values are set
 * on mount, updated whenever they change, and cleared on unmount so context
 * never leaks into a page the user has left.
 *
 * Safe to call from several components at once — the most recent publish wins
 * and each caller's unmount only retracts its own contribution. Blank strings
 * are dropped, so partially-loaded data can be passed unconditionally:
 *
 * ```ts
 * usePublishAssistContext({
 *   label: requisition?.jobTitle,
 *   attributes: { status: statusLabel, division: requisition?.division?.name },
 * });
 * ```
 */
export const usePublishAssistContext = (
  context: PublishedAssistContext,
): void => {
  const { publish, clear } = useContext(AssistPageContextStoreContext);

  // One identity per calling component instance, kept for its whole lifetime.
  const keyRef = useRef<symbol>();
  if (!keyRef.current) keyRef.current = Symbol("assist-page-context");
  const key = keyRef.current;

  const normalised = normalise(context);
  const signature = signatureOf(normalised);
  const value = useMemo(
    () => normalised,
    // Callers pass inline object literals; the signature is what actually
    // changed, so it — not the fresh object — is the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature],
  );

  useEffect(() => {
    publish(key, value);
  }, [publish, key, value]);

  // Deliberately separate from the publish effect: this must run on unmount
  // only, never between value updates (which would blank the context briefly).
  useEffect(() => () => clear(key), [clear, key]);
};
